import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import moment from 'moment';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { makeStyles } from '@mui/styles';
import { Card, CardContent,	TextField, Grid, IconButton, colors, Tooltip } from '@mui/material';
import Create from '@mui/icons-material/Create';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import EditIcon from '@mui/icons-material/EditOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import { Label, DataTable, Header, Alert } from 'components';

import { ToggleIncidentTrackerDialog } from '../../../actions/IncidentTrackerActions.js';

const useStyles = makeStyles((theme) => ({
	root: {},
	content: {
		padding: 0,
	},
	inner: {
		minWidth: 700,
	},
	nameCell: {
		display: 'flex',
		alignItems: 'center',
	},
	avatar: {
		height: 42,
		width: 42,
		marginRight: theme.spacing(1),
	},
	actions: {
		padding: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
	errorButton: {
		color: theme.palette.error.main
	},
	infoButton: {
		color: theme.palette.info.main
	}
}));

const IncidentTrackerTable = (props) => {
	const { className, data, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();

	const _onEditIncidentClick = (incidentId, incidentSubmitter) => {
		dispatch(ToggleIncidentTrackerDialog(true, incidentId, incidentSubmitter));
	};

	const _onDeleteIncidentClick = (incidentId) => {
	};

	const _renderActions = (tableMeta) => {
		const { rowData } = tableMeta;
		return (
			<>
				<Tooltip title="Edit">
					<IconButton edge="end" size="small" className={classes.primaryButton} onClick={() => _onEditIncidentClick(rowData[0], rowData[1])}>
						<EditIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Remove">
					<IconButton edge="end" size="small" disabled className={classes.errorButton} onClick={() => _onEditIncidentClick(rowData[0], rowData[1])}>
						<CancelIcon />
					</IconButton>
				</Tooltip>
			</>
		);
	};

	const _getTableColumns = () => (
		[
			{
				name: 'id',
				label: 'Id',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'submitter',
				label: 'Submitter',
				options: {
					display: false,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Submitter - ${v}`
					},
				}
			},
			{
				name: 'key',
				label: 'Key',
				options: {
					display: false,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Key - ${v}`
					},
				}
			},
			{
				name: 'siteId',
				label: 'Site Id',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Site - ${v}`
					},
				}
			},
			{
				name: 'siteTrackerPRef',
				label: 'SiteTrackerPRef',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `SiteTrackerPRef - ${v}`
					},
				}
			},
			{
				name: 'project',
				label: 'Project',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `Project - ${v}`
					},
				}
			},
			{
				name: 'sow',
				label: 'SOW',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `SOW - ${v}`
					},
				}
			},
			{
				name: 'bo',
				label: 'BO',
				options: {
					display: false,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `BO - ${v}`
					},
				}
			},
			{
				name: 'genericType',
				label: 'Generic Type',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `Generic Type - ${v}`
					},
				}
			},
			{
				name: 'type',
				label: 'Type',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `Type - ${v}`
					},
				}
			},
			{
				name: 'detailedType',
				label: 'DetailedType',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `DetailedType - ${v}`
					},
				}
			},
			{
				name: 'currentQueue',
				label: 'Current Queue',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Current Queue - ${v}`
					},
				}
			},
			{
				name: 'incidentNumber',
				label: 'Incident Number',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Incident - ${v}`
					},
				}
			},
			{
				name: 'taskNumber',
				label: 'Task Number',
				options: {
					display: false,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Task Number - ${v}`
					},
				}
			},
			{
				name: 'status',
				label: 'Status',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `Status - ${v}`
					},
				}
			},
			{
				name: 'priority',
				label: 'Priority',
				options: {
					display: false,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `Priority - ${v}`
					},
				}
			},
			{
				name: 'partner',
				label: 'Partner',
				options: {
					display: false,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `Partner - ${v}`
					},
				}
			},
			{
				name: 'client',
				label: 'Client',
				options: {
					display: false,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `Client - ${v}`
					},
				}
			},
			{
				name: 'actionStatus',
				label: 'ActionStatus',
				options: {
					display: true,
					filterType: 'dropdown',
					customFilterListOptions: {
						render: v => `ActionStatus - ${v}`
					},
				}
			},
			{
				name: 'openDate',
				label: 'Open Date',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Open Date - ${v}`
					},
					customBodyRender: (value, tableMeta, updateValue) => <>{moment(value).format('DD-MM-YYYY HH:mm:ss').toString()}</>
				}
			},
			{
				name: 'closeDate',
				label: 'Close Date',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Close Date - ${v}`
					},
					customBodyRender: (value, tableMeta, updateValue) => <>{moment(value).format('DD-MM-YYYY HH:mm:ss').toString()}</>
				}
			},
			{
				name: 'action',
				label: 'Actions',
				options: {
					filter: false,
					sort: false,
					viewColumns: false,
					empty: true,
					customBodyRender: (value, tableMeta, updateValue) => _renderActions(tableMeta)
				}
			},
		]
	);

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			<DataTable
				id="incidentDataTable"
				title=""
				data={data}
				columns={_getTableColumns()}
				rowsPerPage={20}
				tableBodyMaxHeight="30em"
				tableBodyCellMaxWidth="250"
			/>
		</div>
	);
};

IncidentTrackerTable.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array.isRequired,
};

export default IncidentTrackerTable;
